/**
 * Invitaciones enviadas — vivía en Configuración → Invitaciones.
 *
 * Se muda junto a Mi equipo porque es la misma tarea partida en dos menús: el
 * botón para invitar ya estaba en Mi equipo, y para ver si esa invitación
 * seguía pendiente había que salir a Configuración. Ahora invitas y ves el
 * estado en la misma pantalla.
 *
 * Se recarga sola cuando `refrescar` cambia, que es lo que TeamManager mueve al
 * crear una invitación nueva — así aparece en la lista sin recargar la página.
 */
import { useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Mail, Copy, Trash2, Check, X } from 'lucide-react';

const ESTADOS = {
  pending: 'Pendiente', accepted: 'Aceptada',
  expired: 'Expirada', revoked: 'Cancelada',
};
const PUESTOS = {
  tenant_user: 'Vendedor', tenant_manager: 'Gerente', tenant_admin: 'Admin',
  tenant_owner: 'Dueño', tenant_cajero: 'Cajero',
};

export default function InvitacionesEnviadas({ refrescar }) {
  const [invitaciones, setInvitaciones] = useState([]);

  const cargar = useCallback(() => {
    api.get('/tenant/invitations').then(r => setInvitaciones(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { cargar(); }, [cargar, refrescar]);

  const cancelar = async (id) => {
    try {
      await api.delete(`/tenant/invitations/${id}`);
      toast.success('Invitación cancelada');
      cargar();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pendientes = invitaciones.filter(i => i.status === 'pending').length;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-6" data-testid="invitaciones-enviadas">
      <h3 className="text-lg font-semibold text-[#0F172A] mb-1">
        Invitaciones enviadas{pendientes > 0 && (
          <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {pendientes} sin aceptar
          </span>
        )}
      </h3>
      <p className="text-sm text-[#64748B] mb-4">
        Quién falta por entrar. Si a alguien no le llegó el correo, puedes copiarle el enlace y mandárselo tú.
      </p>

      {invitaciones.length === 0 ? (
        <div className="text-center py-8">
          <Mail className="w-10 h-10 text-[#E2E8F0] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Todavía no has invitado a nadie</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invitaciones.map(inv => (
            <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 border border-[#E2E8F0] rounded-lg" data-testid={`invite-row-${inv.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#0F172A] truncate">{inv.email}</p>
                <p className="text-xs text-[#64748B]">
                  {inv.invitation_metadata?.visible_role
                    || PUESTOS[inv.tenant_role]
                    || inv.tenant_role?.replace(/_/g, ' ')} &middot;{' '}
                  <span className={inv.status === 'pending' ? 'text-amber-600'
                    : inv.status === 'accepted' ? 'text-emerald-600' : 'text-[#94A3B8]'}>
                    {ESTADOS[inv.status] || inv.status}
                  </span>
                  {inv.expires_at && ` · Vence: ${new Date(inv.expires_at).toLocaleDateString('es-MX')}`}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                {inv.status === 'pending' && (
                  <>
                    <Button variant="ghost" size="sm" title="Copiar enlace de invitación"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/invite/${inv.token}`);
                        toast.success('Enlace copiado');
                      }}
                      data-testid={`copy-link-${inv.id}`}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => cancelar(inv.id)}
                      className="text-red-500" title="Cancelar invitación"
                      data-testid={`cancel-invite-${inv.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
                {inv.status === 'accepted' && <Check className="w-4 h-4 text-emerald-500" />}
                {inv.status === 'expired' && <X className="w-4 h-4 text-[#94A3B8]" />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
